import { FunctionComponent, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { faChevronDown, faChevronUp } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { IsolatedHtml } from "@/components/IsolatedHtml/IsolatedHtml";
import { messageBodyToHtml } from "@/utils/messageBodyToHtml";
import { classNames } from "@/utils/classNames";

import { splitEmailHistory } from "../utils/splitEmailHistory.util";

interface EmailMessageContentProps {
	html: string;
	className?: string;
}

// Height at which a long message body is clamped (~10 lines). The full content
// height (`scrollHeight`) is compared against this to decide whether the
// "Show full message" toggle is needed — `scrollHeight` reports the natural
// height even while `max-height` visually clips the element.
const CLAMP_HEIGHT_PX = 240;

export const EmailMessageContent: FunctionComponent<EmailMessageContentProps> = ({
	html,
	className,
}) => {
	const { t } = useTranslation();
	const [showHistory, setShowHistory] = useState(false);
	const [expanded, setExpanded] = useState(false);
	const [isOverflowing, setIsOverflowing] = useState(false);
	const bodyRef = useRef<HTMLDivElement>(null);

	const { latestMessage, history } = useMemo(() => {
		// messageBodyToHtml runs BEFORE the split so everything downstream — the
		// quote/signature detection and both IsolatedHtml renders — sees one
		// consistent shape. A plain-text body (every LinkedIn message, and any
		// not-yet-sent draft) would otherwise reach innerHTML with its line
		// breaks intact but invisible, rendering as one line (PREC-3956).
		return splitEmailHistory(messageBodyToHtml(html?.replaceAll("\\n", "")));
	}, [html]);

	useLayoutEffect(() => {
		const el = bodyRef.current;

		if (el) {
			setIsOverflowing(el.scrollHeight > CLAMP_HEIGHT_PX + 8);
		}
	}, [latestMessage]);

	const clamped = isOverflowing && !expanded;

	return (
		<div className={classNames("flex flex-col", className)}>
			<div
				ref={bodyRef}
				className={classNames(
					"inline-flex flex-col transition-[max-height] duration-200",
					clamped &&
						"max-h-[240px] overflow-hidden [mask-image:linear-gradient(to_bottom,black_70%,transparent)]"
				)}
			>
				<IsolatedHtml className="inline-flex flex-col" html={latestMessage} />
			</div>
			{isOverflowing && (
				<button
					className="flex items-center gap-1 text-xs text-brand-light hover:text-brand-dark mt-2 cursor-pointer self-start"
					type="button"
					onClick={(e) => {
						e.stopPropagation();
						setExpanded((prev) => !prev);
					}}
				>
					<Icon className="w-3 h-3" icon={expanded ? faChevronUp : faChevronDown} />
					{expanded ? t("inbox.thread.showLess") : t("inbox.thread.showFullMessage")}
				</button>
			)}
			{history && (
				<>
					<button
						className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 mt-2 cursor-pointer self-start"
						type="button"
						onClick={(e) => {
							e.stopPropagation();
							setShowHistory(!showHistory);
						}}
					>
						<Icon className="w-3 h-3" icon={showHistory ? faChevronUp : faChevronDown} />
						{showHistory
							? t("inbox.thread.hideTrimmedContent")
							: t("inbox.thread.showTrimmedContent")}
					</button>
					{showHistory && (
						<IsolatedHtml
							className="mt-2 border-l-2 border-gray-300 pl-3 opacity-70"
							html={history}
						/>
					)}
				</>
			)}
		</div>
	);
};
